import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FeatureFlagsManager } from '../../../../../utils/core/feature-flags-manager';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { OptionLabels } from '../../../skyview/right-menu/components/option-labels';
import { RightPanelHeader } from '../../../skyview/right-menu/components/right-menu-header';
import { Stack } from '../../../stack/stack';
import { CadDrawingPdfReportMenu } from './cad-drawing-pdf-report/cad-drawing-pdf-report-menu';
import { CadDrawingReportMenu } from './cad-drawing-pdf-report/cad-drawing-report-menu';
import { PageSettings } from './cad-drawing-pdf-report/page-settings';
import { DesignQuantityReportMenu } from './design-quantity-report/design-quantity-report-menu';
import { PrismPdfReportMenu } from './prism-pdf-report/prism-pdf-report-menu';
import { VolumeOverviewCsvReportMenu } from './volume-overview-csv-report/volume-overview-csv-report-menu';
import { VolumeOverviewPdfReportMenu } from './volume-overview-pdf-report/volume-overview-pdf-report-menu';

type ReportType =
  | 'volumeOverviewPdf'
  | 'volumeOverviewCsv'
  | 'prism'
  | 'cadDrawing'
  | 'designQuantityReport';

export const ReportToolMenu = () => {
  const { t } = useTranslation();
  const [reportType, setReportType] = React.useState<ReportType>('cadDrawing');
  const [cadDrawingPage, setCadDrawingPage] = React.useState<PageSettings>();
  const isBackendCadDrawingReport = FeatureFlagsManager.instance.isEnabled(
    'SK-7544-backend-pdf-report',
  );

  return (
    <Component spacing={0}>
      <RightPanelHeader icon={['fal', 'file-alt']} name={t('title', { ns: 'skyviewReport' })} />

      <Margin>
        <LabelledContainer text={t('reportTypeSection.header', { ns: 'skyviewReport' })}>
          <OptionLabels
            active={reportType}
            itemRows={[
              [
                {
                  title: t('reportTypeSection.cadDrawing', { ns: 'skyviewReport' }),
                  value: 'cadDrawing',
                  onSelect: () => setReportType('cadDrawing'),
                  tooltipText: t('reportTypeSection.cadDrawingTooltip', {
                    ns: 'skyviewReport',
                  }),
                },
              ],
              [
                {
                  title: t('reportTypeSection.volumeOverviewPdf', {
                    ns: 'skyviewReport',
                  }),
                  value: 'volumeOverviewPdf',
                  onSelect: () => setReportType('volumeOverviewPdf'),
                  tooltipText: t('reportTypeSection.volumeOverviewPdfTooltip', {
                    ns: 'skyviewReport',
                  }),
                },
              ],
              [
                {
                  title: t('reportTypeSection.volumeOverviewCsv', {
                    ns: 'skyviewReport',
                  }),
                  value: 'volumeOverviewCsv',
                  onSelect: () => setReportType('volumeOverviewCsv'),
                  tooltipText: t('reportTypeSection.volumeOverviewCsvTooltip', {
                    ns: 'skyviewReport',
                  }),
                },
              ],
              [
                {
                  title: t('reportTypeSection.prism', { ns: 'skyviewReport' }),
                  value: 'prism',
                  onSelect: () => setReportType('prism'),
                  tooltipText: t('reportTypeSection.prismTooltip', {
                    ns: 'skyviewReport',
                  }),
                },
              ],
              [
                {
                  title: t('reportTypeSection.designQuantityReport', { ns: 'skyviewReport' }),
                  value: 'designQuantityReport',
                  onSelect: () => setReportType('designQuantityReport'),
                  tooltipText: t('reportTypeSection.designQuantityReportTooltip', {
                    ns: 'skyviewReport',
                  }),
                },
              ],
            ]}
          />
        </LabelledContainer>
      </Margin>

      {reportType === 'cadDrawing' &&
        (isBackendCadDrawingReport ? (
          <CadDrawingReportMenu page={cadDrawingPage} onPageChange={setCadDrawingPage} />
        ) : (
          <CadDrawingPdfReportMenu />
        ))}
      {reportType === 'volumeOverviewPdf' && <VolumeOverviewPdfReportMenu />}
      {reportType === 'volumeOverviewCsv' && <VolumeOverviewCsvReportMenu />}
      {reportType === 'prism' && <PrismPdfReportMenu />}
      {reportType === 'designQuantityReport' && <DesignQuantityReportMenu />}
    </Component>
  );
};

const Margin = styled.div`
  margin: 0.8em;
`;

const Component = styled(Stack)`
  height: 100%;
`;
